import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SiqNotification } from '@models/app/helpers.model';
import { CognitoLoginData, CognitoQuery, CognitoRefreshToken } from '@models/auth/cognito.model';
import { Bed } from '@models/bed/bed.model';
import { Store } from '@ngxs/store';
import { SiqCommonModule } from '@shared/siq-common.module';
import { InfoBarComponent } from '@shared/standalone-components/info-bar/info-bar.component';
import { LeftNavigationItemComponent } from '@shared/standalone-components/navigation/left-navigation-menu/left-navigation-item/left-navigation-item.component';
import { LeftNavigationMenuComponent } from '@shared/standalone-components/navigation/left-navigation-menu/left-navigation-menu.component';
import { SiqFooterComponent } from '@shared/standalone-components/siq-footer/siq-footer.component';
import { SiqNotificationComponent } from '@shared/standalone-components/siq-notification/siq-notification.component';
import { SiqPrivacyBannerComponent } from '@shared/standalone-components/siq-privacy-banner/siq-privacy-banner.component';
import { PrivacyMode } from '@shared/utils/helpers/enum.helper';
import { SetPrivacyModeBannerState } from '@store/app/app.actions';
import { AppSelectors } from '@store/app/app.selectors';
import { AuthSelectors } from '@store/auth/auth.selectors';
import { BedsSelectors } from '@store/beds/beds.selectors';
import { Observable, Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: true,
  imports: [
    SiqCommonModule, LeftNavigationMenuComponent, LeftNavigationItemComponent, SiqNotificationComponent, 
    SiqPrivacyBannerComponent, InfoBarComponent, SiqFooterComponent
  ]
})
export class LayoutComponent implements OnInit, OnDestroy {
  @Input() isValidPasswordRecovery: boolean | null;
  siqNotification$: Observable<SiqNotification | null>;
  privacyBannerState: PrivacyMode;
  selectedSleeperBed: Bed;

  private _unsubscribeAll = new Subject<void>();

  constructor(private router: Router, private store: Store) { }

  ngOnInit(): void {
    this.siqNotification$ = this.store.select(AppSelectors.siqNotification);

    this.store.select(AppSelectors.showPrivacyModeBanner).pipe(
      takeUntil(this._unsubscribeAll)
    ).subscribe(res => this.privacyBannerState = res);

    this.store.select(BedsSelectors.selectedSleeperBed).pipe(
      takeUntil(this._unsubscribeAll),
      filter((bed): bed is Bed => !!bed)
    ).subscribe(bed => this.selectedSleeperBed = bed);

    // to set privacy mode banner on page refresh
    // case when user clicks later to hide the banner, then refresh - banner should be visible again
    this.store.dispatch(new SetPrivacyModeBannerState(this.privacyModeForSelectedSleeperBed));
  }

  get showLeftNavigationMenu(): boolean {
    return this.checkRoutesToHideLayoutParts(this.store.selectSnapshot(AuthSelectors.cognitoAuth), this.router);
  }

  get showLayoutWithContentInTheMiddle(): boolean {
    return this.checkRouterUrl('auth/login') || this.checkRouterUrl('update-your-password') || this.checkRouterUrl('password-recovery');
  }

  get showFooter(): boolean {
    return this.checkRouterUrl('auth/login') || (this.checkRouterUrl('password-recovery') && this.isValidPasswordRecovery === true);
  }

  get showPrivacyModeBanner(): boolean {
    if(this.checkRouterUrl('pages/select-default-sleeper')) return false;
    return this.privacyBannerState === PrivacyMode.On && this.privacyModeForSelectedSleeperBed === PrivacyMode.On;
  }

  private get privacyModeForSelectedSleeperBed(): PrivacyMode {
    return this.store.selectSnapshot(BedsSelectors.getPrivacyStatusForBed)(this.selectedSleeperBed?.bedId) ?? PrivacyMode.Off;
  }

  private checkRouterUrl(url: string): boolean {
    return this.router.url.includes(url);
  }

  private checkRoutesToHideLayoutParts(isUserLoggedIn: CognitoQuery<CognitoLoginData | CognitoRefreshToken> | null, router: Router): boolean {
    // this function will be used to hide left navigation menu or download apps and DYK sections in the layout
    if (isUserLoggedIn) {
      return !router.url.includes('select-default-sleeper') &&
        !router.url.includes('terms-of-use') &&
        !router.url.includes('register') &&
        !router.url.includes('password-update') &&
        !router.url.includes('pages/setup');
      // since after user sets up password she is logged in into the app
      // by design the registration is handled the same way select-default-sleeper is (no left navigation)
      // same approach should be used for EULA and Privacy
    }
    return false;
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
